import type { TraceOptions } from "./pipeline";
import type { TraceRequest, TraceResponse, TraceSuccess } from "./worker-protocol";

/**
 * Client for the tracing worker.
 *
 * One worker, reused: creating one per request would reload potrace's WASM every
 * time. Requests are numbered, so an old run arriving late is discarded instead
 * of clobbering the newer result.
 */
export class TraceClient {
  private worker: Worker | null = null;
  private nextId = 1;
  private pending = new Map<number, {
    resolve: (r: TraceSuccess) => void;
    reject: (e: Error) => void;
  }>();

  private ensureWorker(): Worker {
    if (!this.worker) {
      this.worker = new Worker(new URL("./trace.worker.ts", import.meta.url), {
        type: "module",
      });
      this.worker.onmessage = (e: MessageEvent<TraceResponse>) => {
        const entry = this.pending.get(e.data.id);
        if (!entry) return; // arrived late: a newer request had already been made
        this.pending.delete(e.data.id);
        if (e.data.ok) entry.resolve(e.data);
        else entry.reject(new Error(e.data.message));
      };
      this.worker.onerror = (e) => {
        const err = new Error(e.message || "the tracing worker failed");
        for (const { reject } of this.pending.values()) reject(err);
        this.pending.clear();
        // The broken worker is dropped: the next request spins up a new one.
        this.worker?.terminate();
        this.worker = null;
      };
    }
    return this.worker;
  }

  trace(gray: Uint8Array, width: number, height: number, opts: TraceOptions): Promise<TraceSuccess> {
    const worker = this.ensureWorker();
    const id = this.nextId++;

    return new Promise<TraceSuccess>((resolve, reject) => {
      this.pending.set(id, { resolve, reject });
      // A COPY is sent: the original buffer stays on the main thread for the next
      // request. Transferring it would leave it empty after the first trace.
      const copy = gray.slice();
      const req: TraceRequest = { id, gray: copy.buffer, width, height, opts };
      worker.postMessage(req, [copy.buffer]);
    });
  }

  /** Forgets a request: its response, if it arrives, is discarded. */
  abandon(id: number) {
    this.pending.delete(id);
  }

  dispose() {
    this.worker?.terminate();
    this.worker = null;
    this.pending.clear();
  }
}
